/**
 * Anchored overlay wrapper for the Find & Replace popover (#2233 — image 24).
 *
 * The popover attaches to the pinned search icon in the editor header.
 * We find that trigger by `[data-action-key="search"]` (emitted by
 * `EditorHeader`), read its bounding rect, and position ourselves right
 * below it. If the trigger isn't in the DOM for any reason (very narrow
 * layout, header not rendered), we fall back to a fixed top-right slot.
 *
 * Rect is recomputed on window resize / scroll and via a `ResizeObserver`
 * on the trigger — so if the header reflows (e.g. sidebar toggles), the
 * popover follows.
 */

import { ReactNode, useEffect, useState } from 'react';

const GAP_PX = 8;
const FALLBACK_TOP = 64;
const FALLBACK_RIGHT = 16;

interface Position {
  top: number;
  right: number;
}

function measure(el: HTMLElement | null): Position {
  if (!el) {
    return { top: FALLBACK_TOP, right: FALLBACK_RIGHT };
  }
  const rect = el.getBoundingClientRect();
  return {
    top: rect.bottom + GAP_PX,
    right: Math.max(FALLBACK_RIGHT, window.innerWidth - rect.right)
  };
}

interface Props {
  /** `data-action-key` value on the trigger element. */
  anchorKey: string;
  children: ReactNode;
}

export function AnchoredOverlay({ anchorKey, children }: Props) {
  const [pos, setPos] = useState<Position>(() => ({
    top: FALLBACK_TOP,
    right: FALLBACK_RIGHT
  }));

  useEffect(() => {
    const findTrigger = () =>
      document.querySelector<HTMLElement>(`[data-action-key="${anchorKey}"]`);
    const update = () => setPos(measure(findTrigger()));
    update();

    const onWindow = () => update();
    window.addEventListener('resize', onWindow);
    window.addEventListener('scroll', onWindow, true);

    const trigger = findTrigger();
    const ro = trigger ? new ResizeObserver(update) : undefined;
    if (trigger && ro) ro.observe(trigger);

    return () => {
      window.removeEventListener('resize', onWindow);
      window.removeEventListener('scroll', onWindow, true);
      ro?.disconnect();
    };
  }, [anchorKey]);

  // Cap the overlay height to what's left under the trigger so the
  // dialog's footer stays reachable on short laptop viewports — the
  // Find/Replace actions used to fall below the fold on 13" screens
  // (#2329). Leave a small margin at the bottom so the shadow doesn't
  // touch the browser's bottom chrome.
  const maxHeight = `calc(100vh - ${pos.top + GAP_PX * 2}px)`;

  return (
    <div
      className="fixed z-[70] flex flex-col"
      style={{ top: pos.top, right: pos.right, maxHeight }}
    >
      {children}
    </div>
  );
}
